import { http, notify } from 'utils'
import { checkEmail } from 'utils/errorHelpers'
import { pick, findIndex, isObject, concat } from 'lodash'
import Vue from 'vue'

import { SET_INTERNS, SET_INTERNS_COUNTS, SET_INTERNS_PAGINATION, ADD_INTERNS, RESET_INTERNS, REMOVE_INTERN } from 'vx/mutations'

// Intern state.
const state = {
  intern: null,
  interns: [],
  internCounts: {
    all: 0,
    active: 0,
    inactive: 0
  },
  pagination: {}
}

// Intern state getters.
const getters = {
  /**
   * Determine if we have a single talent.
   *
   * @return bool
   */
  haveIntern: (state) => {
    return state.booking !== null &&
      state.intern !== null
  },

  /**
   * Determine if we have no more interns to load.
   *
   * @return bool
   */
  haveNoMoreInterns: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Talent actions
const actions = {
  getInternCounts ({ commit }, params = {}) {
    return http.get('intern-counts', { params })
      .then((response) => {
        commit(SET_INTERNS_COUNTS, response.data)

        return response
      })
  },

  getInterns ({ commit }, params = {}) {
    commit(RESET_INTERNS)

    return http.get('interns', { params })
      .then((response) => {
        commit(SET_INTERNS, response.data.data)
        commit(SET_INTERNS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Delete a user.
   *
   * @return Promise
   */
  deleteIntern ({ commit }, { id }) {
    return http.delete(`/intern/${id}`)
      .then((response) => {
        commit(REMOVE_INTERN, response.data)
      })
  },

  /**
   * Get next set of paginated talents.
   *
   * @return Promise
   */
  getNextInterns ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreInterns) {
      return Promise.reject(new Error('There are no more interns to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`interns`, { params })
      .then((response) => {
        commit(ADD_INTERNS, response.data.data)
        commit(SET_INTERNS_PAGINATION, response.data.meta)

        return response
      })
  },

  storeIntern (store, payload) {
    return http.post(`intern`, payload)
      .catch(error => {
        if (checkEmail(error)) {
          notify.error('That email has already been taken.')
        } else {
          notify.error('Something went wrong. Please check your input and try again.')
        }
      })
  },

  resetInterns ({ commit }) {
    commit(RESET_INTERNS)
  }
}

// Talent state mutations.
const mutations = {

  [SET_INTERNS] (state, interns) {
    state.interns = interns
  },

  [SET_INTERNS_COUNTS] (state, internCounts) {
    state.internCounts = internCounts
  },

  [SET_INTERNS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_INTERNS] (state, interns) {
    state.interns = concat(state.interns, interns)
  },

  [RESET_INTERNS] (state, interns) {
    state.interns = []
    state.pagination = {}
  },

  [REMOVE_INTERN] (state, intern) {
    let existingIndex

    if (isObject(intern)) {
      existingIndex = findIndex(state.interns, existing => existing.id === intern.id)
    } else {
      existingIndex = findIndex(state.interns, existing => existing.id === intern)
    }

    if (existingIndex !== -1) {
      Vue.set(state.interns, existingIndex, intern)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
